// Field/table schemas for the generic CRUD DataTable.
// Each field: { key, type, label:{fr,en}, required?, options?, ref?, money? }
// types: text | number | money | date | textarea | select | multiselect | ref

const L = (fr, en) => ({ fr, en });

export const ROLE_OPTIONS = [
  { value: "client", label: L("Client", "Client") },
  { value: "tenant", label: L("Locataire", "Tenant") },
  { value: "supplier", label: L("Fournisseur", "Supplier") },
  { value: "partner", label: L("Partenaire", "Partner") },
];

const STATUS = {
  invoice: [
    { value: "pending", label: L("En attente", "Pending") },
    { value: "sent", label: L("Envoyée", "Sent") },
    { value: "paid", label: L("Payée", "Paid") },
    { value: "late", label: L("En retard", "Late") },
  ],
  rent: [
    { value: "paid", label: L("Payé", "Paid") },
    { value: "pending", label: L("En attente", "Pending") },
  ],
  task: [
    { value: "open", label: L("Ouverte", "Open") },
    { value: "done", label: L("Faite", "Done") },
  ],
  project: [
    { value: "active", label: L("Active", "Active") },
    { value: "done", label: L("Terminé", "Done") },
  ],
  campaign: [
    { value: "active", label: L("Active", "Active") },
    { value: "paused", label: L("En pause", "Paused") },
  ],
  order: [
    { value: "paid", label: L("Payée", "Paid") },
    { value: "refunded", label: L("Remboursée", "Refunded") },
  ],
  maintenance: [
    { value: "open", label: L("Ouverte", "Open") },
    { value: "done", label: L("Terminé", "Done") },
  ],
};

export const RECURRENCE = [
  { value: "none", label: L("Une fois", "Once") },
  { value: "weekly", label: L("Hebdomadaire", "Weekly") },
  { value: "monthly", label: L("Mensuel", "Monthly") },
  { value: "quarterly", label: L("Trimestriel", "Quarterly") },
  { value: "annually", label: L("Annuel", "Annually") },
];

export const SCHEMAS = {
  contacts: {
    columns: ["name", "title", "company_id", "email", "phone"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Nom", "Name") },
      { key: "title", type: "text", label: L("Fonction", "Job title") },
      { key: "company_id", type: "ref", ref: "companies", label: L("Société", "Company") },
      { key: "email", type: "text", label: L("Email", "Email") },
      { key: "phone", type: "text", label: L("Téléphone", "Phone") },
      { key: "roles", type: "multiselect", options: ROLE_OPTIONS, label: L("Rôles", "Roles") },
      { key: "notes", type: "textarea", label: L("Notes", "Notes") },
    ],
  },
  companies: {
    columns: ["name", "industry", "location", "domain"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Société", "Company") },
      { key: "domain", type: "text", label: L("Site web", "Website") },
      { key: "industry", type: "text", label: L("Secteur", "Industry") },
      { key: "location", type: "text", label: L("Localisation", "Location") },
      { key: "notes", type: "textarea", label: L("Notes", "Notes") },
    ],
  },
  documents: {
    columns: ["name", "type", "entity_id", "expiry_date", "status"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Nom", "Name") },
      { key: "type", type: "text", label: L("Type", "Type") },
      { key: "entity_id", type: "ref", ref: "entities", label: L("Entité", "Entity") },
      { key: "contact_id", type: "ref", ref: "contacts", label: L("Contact lié", "Linked contact") },
      { key: "expiry_date", type: "date", label: L("Expiration", "Expiry") },
      { key: "status", type: "text", label: L("Statut", "Status") },
      { key: "url", type: "text", label: L("Lien", "Link") },
    ],
  },
  holdings: {
    columns: ["name", "type", "value", "change_pct"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Ligne", "Holding") },
      { key: "type", type: "text", label: L("Type", "Type") },
      { key: "value", type: "money", label: L("Valeur", "Value") },
      { key: "change_pct", type: "number", label: L("Variation %", "Change %") },
    ],
  },
  invoices: {
    columns: ["number", "client_id", "amount", "status", "due_date"],
    fields: [
      { key: "number", type: "text", required: true, label: L("N° facture", "Invoice #") },
      { key: "client_id", type: "ref", ref: "companies", label: L("Client", "Client") },
      { key: "project_id", type: "ref", ref: "space_projects", label: L("Projet", "Project") },
      { key: "amount", type: "money", required: true, label: L("Montant", "Amount") },
      { key: "status", type: "select", options: STATUS.invoice, label: L("Statut", "Status") },
      { key: "issue_date", type: "date", label: L("Émise le", "Issued") },
      { key: "due_date", type: "date", label: L("Échéance", "Due date") },
    ],
  },
  products: {
    columns: ["sku", "name", "stock", "price", "margin_pct"],
    fields: [
      { key: "sku", type: "text", required: true, label: L("SKU", "SKU") },
      { key: "name", type: "text", required: true, label: L("Produit", "Product") },
      { key: "stock", type: "number", label: L("Stock", "Stock") },
      { key: "price", type: "money", label: L("Prix", "Price") },
      { key: "margin_pct", type: "number", label: L("Marge %", "Margin %") },
      { key: "supplier_id", type: "ref", ref: "companies", label: L("Fournisseur", "Supplier") },
    ],
  },
  orders: {
    columns: ["number", "amount", "status", "date"],
    fields: [
      { key: "number", type: "text", label: L("N° commande", "Order #") },
      { key: "amount", type: "money", required: true, label: L("Montant", "Amount") },
      { key: "status", type: "select", options: STATUS.order, label: L("Statut", "Status") },
      { key: "date", type: "date", label: L("Date", "Date") },
    ],
  },
  suppliers: {
    columns: ["name", "category", "lead_time_days"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Fournisseur", "Supplier") },
      { key: "contact_id", type: "ref", ref: "contacts", label: L("Contact", "Contact") },
      { key: "category", type: "text", label: L("Catégorie", "Category") },
      { key: "lead_time_days", type: "number", label: L("Délai (j)", "Lead time (d)") },
    ],
  },
  campaigns: {
    columns: ["name", "channel", "status", "budget"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Campagne", "Campaign") },
      { key: "channel", type: "text", label: L("Canal", "Channel") },
      { key: "status", type: "select", options: STATUS.campaign, label: L("Statut", "Status") },
      { key: "budget", type: "money", label: L("Budget", "Budget") },
    ],
  },
  properties: {
    columns: ["name", "type", "address", "value"],
    fields: [
      { key: "name", type: "text", required: true, label: L("Bien", "Property") },
      { key: "type", type: "text", label: L("Type", "Type") },
      { key: "address", type: "text", label: L("Adresse", "Address") },
      { key: "value", type: "money", label: L("Valeur", "Value") },
    ],
  },
  tenants: {
    columns: ["contact_id", "property_id", "rent_amount", "since"],
    fields: [
      { key: "contact_id", type: "ref", ref: "contacts", required: true, label: L("Locataire", "Tenant") },
      { key: "property_id", type: "ref", ref: "properties", label: L("Bien", "Property") },
      { key: "rent_amount", type: "money", label: L("Loyer", "Rent") },
      { key: "since", type: "date", label: L("Depuis", "Since") },
    ],
  },
  rents: {
    columns: ["label", "month", "amount", "status", "due_date"],
    fields: [
      { key: "label", type: "text", required: true, label: L("Libellé", "Label") },
      { key: "property_id", type: "ref", ref: "properties", label: L("Bien", "Property") },
      { key: "month", type: "text", label: L("Mois (YYYY-MM)", "Month (YYYY-MM)") },
      { key: "amount", type: "money", label: L("Montant", "Amount") },
      { key: "status", type: "select", options: STATUS.rent, label: L("Statut", "Status") },
      { key: "due_date", type: "date", label: L("Échéance", "Due date") },
    ],
  },
  loans: {
    columns: ["label", "remaining", "rate", "monthly_payment"],
    fields: [
      { key: "label", type: "text", required: true, label: L("Prêt", "Loan") },
      { key: "property_id", type: "ref", ref: "properties", label: L("Bien", "Property") },
      { key: "principal", type: "money", label: L("Capital initial", "Principal") },
      { key: "remaining", type: "money", label: L("Restant dû", "Remaining") },
      { key: "rate", type: "number", label: L("Taux %", "Rate %") },
      { key: "monthly_payment", type: "money", label: L("Mensualité", "Monthly") },
    ],
  },
  maintenance: {
    columns: ["title", "property_id", "status", "cost"],
    fields: [
      { key: "title", type: "text", required: true, label: L("Intervention", "Task") },
      { key: "property_id", type: "ref", ref: "properties", label: L("Bien", "Property") },
      { key: "status", type: "select", options: STATUS.maintenance, label: L("Statut", "Status") },
      { key: "cost", type: "money", label: L("Coût", "Cost") },
      { key: "date", type: "date", label: L("Date", "Date") },
    ],
  },
  tasks: {
    columns: ["title", "status", "recurrence", "due_date", "impact", "effort"],
    fields: [
      { key: "title", type: "text", required: true, label: L("Tâche", "Task") },
      { key: "status", type: "select", options: STATUS.task, label: L("Statut", "Status") },
      { key: "recurrence", type: "select", options: RECURRENCE, label: L("Récurrence", "Recurrence") },
      { key: "due_date", type: "date", label: L("Échéance", "Due date") },
      { key: "key_result_id", type: "ref", ref: "key_results", label: L("Résultat clé", "Key result") },
      { key: "impact", type: "number", label: L("Impact (1-10)", "Impact (1-10)") },
      { key: "effort", type: "number", label: L("Effort (1-10)", "Effort (1-10)") },
    ],
  },
  objectives: {
    columns: ["title", "quarter", "description"],
    fields: [
      { key: "title", type: "text", required: true, label: L("Objectif", "Objective") },
      { key: "entity_id", type: "ref", ref: "entities", label: L("Entité", "Entity") },
      { key: "quarter", type: "text", label: L("Trimestre", "Quarter") },
      { key: "description", type: "textarea", label: L("Description", "Description") },
    ],
  },
  key_results: {
    columns: ["title", "current", "target", "unit", "impact", "effort"],
    fields: [
      { key: "title", type: "text", required: true, label: L("Résultat clé", "Key result") },
      { key: "objective_id", type: "ref", ref: "objectives", label: L("Objectif", "Objective") },
      { key: "entity_id", type: "ref", ref: "entities", label: L("Entité", "Entity") },
      { key: "current", type: "number", label: L("Actuel", "Current") },
      { key: "target", type: "number", label: L("Cible", "Target") },
      { key: "unit", type: "text", label: L("Unité", "Unit") },
      { key: "impact", type: "number", label: L("Impact (1-10)", "Impact (1-10)") },
      { key: "effort", type: "number", label: L("Effort (1-10)", "Effort (1-10)") },
    ],
  },
};

export const ENTITY_TABLES = {
  holding: ["holdings", "tasks", "documents"],
  consulting: ["invoices", "tasks", "documents"],
  ecommerce: ["products", "orders", "suppliers", "campaigns", "tasks", "documents"],
  sci: ["properties", "tenants", "rents", "loans", "maintenance", "tasks", "documents"],
};

export const MONEY_KEYS = new Set([
  "amount", "value", "price", "budget", "rent_amount", "monthly_payment",
  "principal", "remaining", "cost",
]);

export const STATUS_KEYS = new Set(["status"]);

export function displayName(resource, doc) {
  if (!doc) return "—";
  return doc.name || doc.title || doc.label || doc.number || doc.sku || "—";
}
